import { beginNegotiation } from '~/features/market/negotiations/beginNegotiation';
import { MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { loadAreasOfInterest } from '~/features/provider-hlx/service-listings/aoiApi';
import type { OptionStringType } from '~/features/vault/profile/types';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { getData } from '~/utils/service';
import { ProviderServiceListingSearchResponseSchema } from './schemas';

export type ListingKindFilter = 'LOCUMS' | 'PART_TIME' | 'FULL_TIME';

export type ProviderSearchFilters = {
  start: string;
  /** Omitted unless the search is for Locums; the search then runs from `start` on. */
  end?: string;
  query?: string;
  listingKind?: ListingKindFilter;
  providerCategoryId?: number;
  specialtyId?: number;
  /** All of these (AND); sent as a CSV of area-of-interest ids. */
  subspecialtyIds?: number[];
  shiftLabel?: string;
  shiftType?: string;
  workLocation?: string;
  /** Any of these license states (OR); sent as a CSV of two-letter codes (MCV-1865, MCV-1950). */
  stateCodes?: string[];
  /** Only with an On-site or Hybrid work location; both or neither (MCV-1863). */
  nearLat?: number;
  nearLng?: number;
  providerServiceListingId?: number;
};

export const searchProviderServiceListings = async (
  filters: ProviderSearchFilters,
  page = 1,
  pageSize = 20,
  signal?: AbortSignal
) => {
  const params = {
    start: filters.start,
    page: String(page),
    pageSize: String(pageSize),
    ...(filters.end ? { end: filters.end } : {}),
    ...(filters.listingKind ? { listingKind: filters.listingKind } : {}),
    ...(filters.providerCategoryId !== undefined
      ? { providerCategoryId: String(filters.providerCategoryId) }
      : {}),
    ...(filters.specialtyId !== undefined ? { specialtyId: String(filters.specialtyId) } : {}),
    ...(filters.subspecialtyIds?.length
      ? { subspecialtyIds: filters.subspecialtyIds.join(',') }
      : {}),
    ...(filters.query ? { query: filters.query } : {}),
    ...(filters.shiftLabel ? { shiftLabel: filters.shiftLabel } : {}),
    ...(filters.shiftType ? { shiftType: filters.shiftType } : {}),
    ...(filters.workLocation ? { workLocation: filters.workLocation } : {}),
    ...(filters.stateCodes?.length
      ? { stateCodes: filters.stateCodes.map((code) => code.toUpperCase()).join(',') }
      : {}),
    ...(filters.nearLat !== undefined && filters.nearLng !== undefined
      ? { nearLat: String(filters.nearLat), nearLng: String(filters.nearLng) }
      : {}),
    ...(filters.providerServiceListingId !== undefined
      ? { providerServiceListingId: String(filters.providerServiceListingId) }
      : {}),
  };
  return await withRequestDeadline(
    async (requestSignal) => {
      const response = await getData<unknown>('providerServiceListingSearch', {
        params,
        signal: requestSignal,
      });
      return ProviderServiceListingSearchResponseSchema.parse(response);
    },
    MARKET_READ_TIMEOUT_MS,
    signal
  );
};

/** A pickable subspecialty (area of interest): the option value is its id. */
export type SubspecialtyOption = OptionStringType;

/**
 * [MCV-1855] The subspecialties a listing can offer under one AOI specialty: the same areas of
 * interest a provider picks in My services. A search sends the picked ids and a listing must
 * have every one.
 */
export const loadSubspecialtyOptions = async (
  aoiSpecialtyId: number,
  signal?: AbortSignal
): Promise<SubspecialtyOption[]> =>
  (await loadAreasOfInterest(aoiSpecialtyId, signal)).map((area) => ({
    value: String(area.id),
    label: area.aoiName,
  }));

export const beginProviderServiceNegotiation = async (
  providerServiceListingId: number,
  availabilityEntryIds: number[],
  signal?: AbortSignal
) =>
  await beginNegotiation(
    {
      listingType: 'SERVICE',
      providerServiceListingId,
      availabilityEntryIds: [...new Set(availabilityEntryIds)].sort((left, right) => left - right),
    },
    signal
  );
